import { type ReactNode, useCallback } from "react";
import type { GestureResponderEvent } from "react-native";
import { AnimatePresence, type ColorTokens, type GetProps, Spinner, Stack, styled, Text, Theme, XStack } from "tamagui";
import { HapticsService, useMinimumLoadingTime } from "../utils";
import { Icon, type IconComponent } from "./Icon";
export type ButtonVariant = "primary" | "secondary" | "tertiary";
export type ButtonSize = "large" | "medium" | "small";
const buttonVariants = {
  variant: {
    primary: {
      backgroundColor: "$brandNavyDark",
      borderWidth: 0,
      hoverStyle: {
        backgroundColor: "$brandNavy"
      },
      pressStyle: {
        backgroundColor: "$brandNavy",
        scale: 0.97
      },
      focusVisibleStyle: {
        outlineWidth: 2,
        outlineColor: "$brandNavy",
        outlineStyle: "solid",
        outlineOffset: 2
      }
    },
    secondary: {
      backgroundColor: "$transparent",
      borderWidth: 1,
      borderColor: "$uiStroke",
      hoverStyle: {
        backgroundColor: "$transparent",
        borderColor: "$textPrimary"
      },
      pressStyle: {
        backgroundColor: "$transparent",
        borderColor: "$textPrimary",
        scale: 0.97
      },
      focusVisibleStyle: {
        outlineWidth: 2,
        outlineColor: "$brandNavy",
        outlineStyle: "solid",
        outlineOffset: 2
      }
    },
    tertiary: {
      backgroundColor: "$transparent",
      borderWidth: 0,
      hoverStyle: {
        backgroundColor: "$surfaceSecondary"
      },
      pressStyle: {
        backgroundColor: "$surfaceSecondary",
        scale: 0.97
      },
      focusVisibleStyle: {
        outlineWidth: 2,
        outlineColor: "$brandNavy",
        outlineStyle: "solid",
        outlineOffset: 2
      }
    }
  },
  size: {
    large: {
      height: 56,
      paddingHorizontal: "$sp4"
    },
    medium: {
      height: 40,
      paddingHorizontal: "$sp3"
    },
    small: {
      height: 32,
      paddingHorizontal: "$sp2"
    }
  },
  disabled: {
    true: {
      cursor: "not-allowed",
      opacity: 1,
      backgroundColor: "$surfaceSecondary",
      borderColor: "$transparent",
      hoverStyle: {
        backgroundColor: "$surfaceSecondary",
        borderColor: "$transparent"
      },
      pressStyle: {
        backgroundColor: "$surfaceSecondary",
        borderColor: "$transparent",
        scale: 1
      }
    }
  },
  fillWidth: {
    true: {
      width: "100%"
    },
    false: {
      width: "auto",
      minWidth: "auto"
    }
  }
} as const;
const StyledButton = styled(Stack, {
  name: "Button",
  tag: "button",
  role: "button",
  focusable: true,
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  borderRadius: "$radiusFull",
  // Use CSS transitions instead of Tamagui's animation system to avoid inline styles
  style: {
    transitionProperty: "background-color, border-color, transform",
    transitionDuration: "100ms",
    transitionTimingFunction: "ease-out"
  },
  variants: buttonVariants,
  defaultVariants: {
    variant: "primary",
    size: "medium",
    fillWidth: true
  }
});
const textVariants = {
  variant: {
    primary: {
      color: "$surfaceWhite"
    },
    secondary: {
      color: "$textPrimary"
    },
    tertiary: {
      color: "$brandNavyDark"
    }
  },
  size: {
    large: {
      fontSize: 16,
      lineHeight: 24
    },
    medium: {
      fontSize: 16,
      lineHeight: 24
    },
    small: {
      fontSize: 14,
      lineHeight: 16
    }
  },
  disabled: {
    true: {
      color: "$textDisabled"
    }
  }
} as const;
const ButtonText = styled(Text, {
  name: "ButtonText",
  tag: "span",
  fontFamily: "$body",
  fontWeight: "500",
  variants: textVariants,
  defaultVariants: {
    variant: "primary",
    size: "medium"
  }
});
const textColorTokens: Record<ButtonVariant, ColorTokens> = {
  primary: "$surfaceWhite",
  secondary: "$textPrimary",
  tertiary: "$brandNavyDark"
};
const buttonSizeToIconToken: Record<ButtonSize, `$icon${Capitalize<ButtonSize>}`> = {
  large: "$iconLarge",
  medium: "$iconMedium",
  small: "$iconSmall"
};
type StyledButtonProps = GetProps<typeof StyledButton>;
export type ButtonProps = Omit<StyledButtonProps, "variant" | "size" | "onPressOut" | "children"> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  iconLeft?: IconComponent;
  iconRight?: IconComponent;
  /**
   * Skip the minimum loading display time (200ms) and transition instantly.
   * @default false
   */
  instantLoading?: boolean;
  children?: ReactNode;
};
export const Button = (props: ButtonProps) => {
  const {
    variant = "primary",
    size = "medium",
    loading,
    instantLoading = false,
    disabled,
    iconLeft,
    iconRight,
    fillWidth = true,
    children,
    onPress,
    ...rest
  } = props;
  const debouncedLoading = useMinimumLoadingTime(loading);
  const showLoading = instantLoading ? loading ?? false : debouncedLoading;
  const handlePressOut = useCallback(() => {
    if (!disabled) {
      void HapticsService.selection();
    }
  }, [disabled]);
  const handlePress = useCallback((event: GestureResponderEvent) => {
    if (!disabled) {
      onPress?.(event);
    }
  }, [disabled, onPress]);
  const iconSize = buttonSizeToIconToken[size];
  const iconColor: ColorTokens = disabled ? "$textDisabled" : textColorTokens[variant];
  return <Theme name="v2" data-sentry-element="Theme" data-sentry-component="Button" data-sentry-source-file="Button.tsx">
      <StyledButton {...rest} variant={variant} size={size} disabled={disabled} fillWidth={fillWidth} onPressOut={handlePressOut} onPress={handlePress} data-sentry-element="StyledButton" data-sentry-source-file="Button.tsx">
        <XStack justifyContent="center" alignItems="center" gap="$sp1" position="relative" data-sentry-element="XStack" data-sentry-source-file="Button.tsx">
          {iconLeft ? <Stack opacity={showLoading ? 0 : 1} animation="quick" animateOnly={["opacity"]}>
              <Icon icon={iconLeft} size={iconSize} color={iconColor} />
            </Stack> : null}
          {children ? <ButtonText variant={variant} size={size} disabled={disabled} opacity={showLoading ? 0 : 1} animation="quick" animateOnly={["opacity"]}>
              {children}
            </ButtonText> : null}
          {iconRight ? <Stack opacity={showLoading ? 0 : 1} animation="quick" animateOnly={["opacity"]}>
              <Icon icon={iconRight} size={iconSize} color={iconColor} />
            </Stack> : null}
          <AnimatePresence data-sentry-element="AnimatePresence" data-sentry-source-file="Button.tsx">
            {showLoading ? <Stack position="absolute" left={0} top={0} right={0} bottom={0} justifyContent="center" alignItems="center" enterStyle={{
            opacity: 0
          }} exitStyle={{
            opacity: 0
          }} animateOnly={["opacity"]} animation="quick" zIndex={100}>
                <Spinner flex={0} color={iconColor} key="loading-spinner" size="small" />
              </Stack> : null}
          </AnimatePresence>
        </XStack>
      </StyledButton>
    </Theme>;
};